import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * @typedef {'loading' | 'available' | 'low' | 'unavailable' | 'unknown' | 'error'} StockStatus
 */

/**
 * Fetches MODDO stock via proxy and shows only stores that have stock.
 * Matches metaobject `id_tienda_externa` with MODDO `idTienda`.
 * Never exposes exact quantities — only status + label.
 * Uses variant SKU (preferred) or barcode, sent as `ean` to the proxy.
 * @extends {Component}
 */
class StoreStockAvailability extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string | null} */
  #lastSku = null;

  /** @type {EventTarget | null} */
  #variantTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#observeDialogOpen();
    this.#bindVariantUpdates();
    this.#bindBackInStock();

    if (this.#isVisible()) {
      this.#loadStock();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#variantTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.querySelector('[data-stock-bis-trigger]')?.removeEventListener('click', this.#onBackInStockClick);
  }

  #isVisible() {
    return this.getClientRects().length > 0;
  }

  #bindVariantUpdates() {
    // Listen on the product section, not the popup <dialog>: variant:update bubbles from
    // the variant-picker outside the dialog and never enters it.
    const target =
      this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, .shopify-section') ||
      document;

    this.#variantTarget = target;
    target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    const detail = event.detail;
    const productId = this.dataset.productId;

    if (
      productId &&
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== productId
    ) {
      return;
    }

    if (detail?.data?.newProduct?.id) {
      this.dataset.productId = String(detail.data.newProduct.id);
    }

    if (detail?.data?.newProduct?.title) {
      this.dataset.productTitle = String(detail.data.newProduct.title);
    }

    const resource = detail?.resource;
    if (resource?.id != null) {
      this.dataset.variantId = String(resource.id);
    }
    if (typeof resource?.available === 'boolean') {
      this.dataset.onlineAvailable = resource.available ? 'true' : 'false';
    }

    const fromHtml = this.#syncFromVariantHtml(event);
    const sku = this.#skuFromVariantEvent(event) || fromHtml.sku;

    if (sku === (this.dataset.sku || '').trim()) {
      if (this.#isVisible()) this.#refreshEmptyState();
      return;
    }

    this.dataset.sku = sku;
    this.#lastSku = null;

    if (this.#isVisible()) {
      this.#loadStock({ force: true });
    }
  };

  /**
   * @param {VariantUpdateEvent} event
   * @returns {{ sku: string }}
   */
  #syncFromVariantHtml(event) {
    const html = event.detail?.data?.html;
    if (!(html instanceof Document)) return { sku: '' };

    const productId = this.dataset.productId;
    const selector = productId
      ? `store-stock-availability[data-product-id="${CSS.escape(productId)}"]`
      : 'store-stock-availability';
    const next = html.querySelector(selector);
    if (!(next instanceof HTMLElement)) return { sku: '' };

    if (next.dataset.variantId) this.dataset.variantId = next.dataset.variantId;
    if (next.dataset.onlineAvailable) this.dataset.onlineAvailable = next.dataset.onlineAvailable;
    if (next.dataset.productTitle) this.dataset.productTitle = next.dataset.productTitle;

    return { sku: (next.dataset.sku || '').trim() };
  }

  /**
   * @param {VariantUpdateEvent} event
   * @returns {string}
   */
  #skuFromVariantEvent(event) {
    const resource = event.detail?.resource;
    const fromSku = String(resource?.sku ?? '').trim();
    const fromBarcode = String(resource?.barcode ?? '').trim();

    return fromSku || fromBarcode;
  }

  #observeDialogOpen() {
    const dialog = this.closest('dialog');

    if (!(dialog instanceof HTMLDialogElement)) return;

    const onToggle = () => {
      if (dialog.open) this.#loadStock();
    };

    dialog.addEventListener('toggle', onToggle);
    const observer = new MutationObserver(() => {
      if (dialog.open) this.#loadStock();
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  /**
   * @param {{ force?: boolean }} [options]
   */
  async #loadStock({ force = false } = {}) {
    const endpoint = this.dataset.endpoint?.trim();
    const sku = this.dataset.sku?.trim() || '';

    if (!endpoint) {
      this.#setGlobalStatus('error');
      return;
    }

    if (!sku) {
      this.#abortController?.abort();
      this.#lastSku = null;
      this.#hideAllRows();
      this.#setGlobalStatus(null);
      this.#setNoneVisible(true);
      return;
    }

    if (!force && this.#lastSku === sku) return;

    this.#setGlobalStatus('loading');
    this.#hideAllRows();
    this.#setNoneVisible(false);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const url = new URL(endpoint, window.location.origin);
    // Proxy param name remains `ean`; value is barcode or SKU (codigoAlfa / EAN MODDO).
    url.searchParams.set('ean', sku);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`stock_http_${response.status}`);
      }

      const data = await response.json();
      const stockByStore = this.#indexStock(data);
      const matchedIds = this.#applyStock(stockByStore);
      const visibleCount = matchedIds.size;
      this.#lastSku = sku;
      this.#setListVisible(visibleCount > 0);
      this.#setGlobalStatus(null);
      this.#setNoneVisible(visibleCount === 0);
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('[store-stock-availability]', error);
      this.#lastSku = null;
      this.#hideAllRows();
      this.#setNoneVisible(false);
      this.#setGlobalStatus('error');
    }
  }

  #isOnlineAvailable() {
    return this.dataset.onlineAvailable !== 'false';
  }

  #refreshEmptyState() {
    const none = this.querySelector('[data-stock-none]');
    if (!(none instanceof HTMLElement) || none.hidden) return;
    this.#setNoneVisible(true);
  }

  #bindBackInStock() {
    this.querySelector('[data-stock-bis-trigger]')?.addEventListener('click', this.#onBackInStockClick);
  }

  #onBackInStockClick = () => {
    const drawer = document.querySelector('back-in-stock-drawer');
    if (
      !(drawer instanceof HTMLElement) ||
      !('openDrawer' in drawer) ||
      typeof drawer.openDrawer !== 'function'
    ) {
      return;
    }

    drawer.openDrawer({
      productId: this.dataset.productId || '',
      variantId: this.dataset.variantId || '',
      productTitle: this.dataset.productTitle || '',
      variantLabel: '',
    });
  };

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeStoreId(value) {
    const raw = String(value ?? '').trim();

    if (!raw) return '';

    const asNumber = Number(raw);

    if (Number.isFinite(asNumber)) return String(asNumber);

    return raw;
  }

  /**
   * @returns {Record<string, string>}
   */
  #parseIdMap() {
    const raw = this.dataset.idMap?.trim();

    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

      /** @type {Record<string, string>} */
      const map = {};

      for (const [key, value] of Object.entries(parsed)) {
        const normalizedKey = String(key).trim().toLowerCase();
        const normalizedValue = this.#normalizeStoreId(value);

        if (normalizedKey && normalizedValue) {
          map[normalizedKey] = normalizedValue;
        }
      }

      return map;
    } catch {
      return {};
    }
  }

  /**
   * @param {HTMLElement} row
   * @returns {string}
   */
  #resolveStoreId(row) {
    const direct = this.#normalizeStoreId(row.dataset.storeIdTienda);

    if (direct) return direct;

    const idMap = this.#parseIdMap();
    const handle = row.dataset.storeHandle?.trim().toLowerCase();
    const name = row.querySelector('.store-stock__name')?.textContent?.trim().toLowerCase();

    if (handle && idMap[handle]) return idMap[handle];
    if (name && idMap[name]) return idMap[name];

    return '';
  }

  /**
   * @param {unknown} data
   * @returns {Map<string, number>}
   */
  #indexStock(data) {
    /** @type {Map<string, number>} */
    const map = new Map();
    const tiendas = data && typeof data === 'object' ? data.tiendas : null;

    if (!Array.isArray(tiendas)) return map;

    for (const item of tiendas) {
      if (!item || typeof item !== 'object') continue;

      const id = this.#normalizeStoreId(item.idTienda);

      if (!id) continue;

      const qty = Number(item.stock);
      map.set(id, Number.isFinite(qty) ? qty : 0);
    }

    return map;
  }

  /**
   * @param {number} qty
   * @returns {StockStatus}
   */
  #statusFromQty(qty) {
    const threshold = Number(this.dataset.lowThreshold || 5);
    const lowThreshold = Number.isFinite(threshold) && threshold > 0 ? threshold : 5;

    if (qty <= 0) return 'unavailable';
    if (qty < lowThreshold) return 'low';

    return 'available';
  }

  /**
   * @param {Map<string, number>} stockByStore
   * @returns {Set<string>}
   */
  #applyStock(stockByStore) {
    const rows = this.querySelectorAll('[data-store-row="shopify"]');
    const matchedIds = new Set();

    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;

      const id = this.#resolveStoreId(row);

      if (id) {
        row.dataset.storeIdTienda = id;
      }

      if (!id || !stockByStore.has(id)) {
        this.#hideRow(row);
        continue;
      }

      const status = this.#statusFromQty(stockByStore.get(id) ?? 0);

      if (status === 'unavailable') {
        this.#hideRow(row);
        continue;
      }

      matchedIds.add(id);
      this.#paintRow(row, status);
      this.#showRow(row);
    }

    return matchedIds;
  }

  #hideAllRows() {
    const rows = this.querySelectorAll('[data-store-row="shopify"]');

    for (const row of rows) {
      if (row instanceof HTMLElement) this.#hideRow(row);
    }

    this.#setListVisible(false);
  }

  /**
   * @param {HTMLElement} row
   */
  #hideRow(row) {
    row.hidden = true;
  }

  /**
   * @param {HTMLElement} row
   */
  #showRow(row) {
    row.hidden = false;
  }

  /**
   * @param {boolean} visible
   */
  #setListVisible(visible) {
    const list = this.querySelector('[data-store-list]');

    if (list instanceof HTMLElement) {
      list.hidden = !visible;
    }
  }

  /**
   * @param {boolean} visible
   */
  #setNoneVisible(visible) {
    const none = this.querySelector('[data-stock-none]');
    const onlineMsg = this.querySelector('[data-stock-none-online]');
    const soldOutMsg = this.querySelector('[data-stock-none-sold-out]');
    const bisButton = this.querySelector('[data-stock-bis-trigger]');

    if (!(none instanceof HTMLElement)) return;

    none.hidden = !visible;

    if (!visible) {
      if (onlineMsg instanceof HTMLElement) onlineMsg.hidden = false;
      if (soldOutMsg instanceof HTMLElement) soldOutMsg.hidden = true;
      if (bisButton instanceof HTMLElement) bisButton.hidden = true;
      return;
    }

    const soldOut = !this.#isOnlineAvailable();

    if (onlineMsg instanceof HTMLElement) onlineMsg.hidden = soldOut;
    if (soldOutMsg instanceof HTMLElement) soldOutMsg.hidden = !soldOut;
    if (bisButton instanceof HTMLElement) bisButton.hidden = !soldOut;
  }

  /**
   * @param {HTMLElement} row
   * @param {StockStatus} status
   */
  #paintRow(row, status) {
    const dot = row.querySelector('[data-stock-dot]');
    const label = row.querySelector('[data-stock-label]');

    if (!(dot instanceof HTMLElement) || !(label instanceof HTMLElement)) return;

    row.dataset.stockStatus = status;
    dot.dataset.status = status;

    const key = `label${status.charAt(0).toUpperCase()}${status.slice(1)}`;
    label.textContent = this.dataset[key] || status;
  }

  /**
   * @param {StockStatus | null} status
   */
  #setGlobalStatus(status) {
    const el = this.querySelector('[data-stock-global-status]');

    if (!(el instanceof HTMLElement)) return;

    if (!status) {
      el.hidden = true;
      el.textContent = '';
      return;
    }

    el.hidden = false;
    el.dataset.status = status;

    if (status === 'loading') {
      el.textContent = this.dataset.labelLoading || '';
    } else if (status === 'error') {
      el.textContent = this.dataset.labelError || '';
    }
  }
}

if (!customElements.get('store-stock-availability')) {
  customElements.define('store-stock-availability', StoreStockAvailability);
}
